import { LocalStoragePrefix } from '@/constants';
import {
  CountryDisplayNames,
  CountryList,
  EducationList,
  EyeColor,
  HairColor,
  MaritalStatus,
  ReligionList,
} from '@/constants/form_inputs';
import {
  AttemptIndicator,
  IAgency,
  ICFSLog,
  IHeightExact,
  IKase,
  IKaseLog,
  IPenalCode,
  IUser,
} from '@/models';

interface LocalStorageGetParams {
  prefix?: LocalStoragePrefix | string;
  key: string;
  shouldParse?: boolean;
}

interface LocalStorageSetParams {
  prefix?: LocalStoragePrefix | string;
  key: string;
  value: any;
}

export const getLocalStorageKey = (
  key: string,
  prefix?: LocalStoragePrefix | string,
) => {
  if (!prefix) return key;
  return `${prefix}-${key}`;
};

export const setLocalStorageItem = ({
  prefix,
  key,
  value,
}: LocalStorageSetParams) => {
  const storageKey = getLocalStorageKey(key, prefix);
  if (typeof value === 'object') {
    localStorage.setItem(storageKey, JSON.stringify(value));
  } else {
    localStorage.setItem(storageKey, value);
  }
};

export const getLocalStorageItem = ({
  prefix,
  key,
  shouldParse,
}: LocalStorageGetParams) => {
  const storageKey = getLocalStorageKey(key, prefix);
  const item = localStorage.getItem(storageKey);
  return shouldParse && item ? JSON.parse(item || '') : item;
};

export const getEffectiveClassAndCategory = (
  penalCode: IPenalCode | null | undefined,
  attemptIndicator?: AttemptIndicator | string | null,
): { statutoryClass?: string; category?: string } => {
  if (!penalCode) return {};
  if (attemptIndicator === AttemptIndicator.ATTEMPTED) {
    // '9'/'0' are DCJS "not applicable" sentinels, not real categories —
    // treat them the same as a missing attempted value.
    const attemptedCategory = penalCode.attemptedNysLawCategory?.trim();
    const hasAttemptedCategory =
      !!attemptedCategory &&
      attemptedCategory !== '9' &&
      attemptedCategory !== '0';
    return {
      statutoryClass:
        penalCode.attemptedClass?.trim() || penalCode.statutoryClass,
      category: hasAttemptedCategory ? attemptedCategory : penalCode.category,
    };
  }
  return {
    statutoryClass: penalCode.statutoryClass,
    category: penalCode.category,
  };
};

export const getPenalCodeLabel = (
  penalCode: IPenalCode,
  attemptIndicator?: AttemptIndicator | string | null,
) => {
  if (!penalCode) return '';

  const { statutoryClass, category } = getEffectiveClassAndCategory(
    penalCode,
    attemptIndicator,
  );
  const firstPart = [
    penalCode.lawTitle,
    penalCode.section,
    penalCode.subsection,
    statutoryClass,
    category,
    penalCode.degree,
    penalCode.ibrCode,
  ]
    .filter((item) => !!item)
    .join(' ');
  return [firstPart, penalCode.miniLawDescription]
    .filter((item) => !!item)
    .join(' - ');
};

export const createUrlWithParams = (
  _baseURL: string,
  params: Record<string, any>,
) => {
  const url = new URL(_baseURL);
  Object.keys(params).forEach((key) =>
    url.searchParams.append(key, params[key]),
  );
  return url.toString();
};

export const filterLogs = <T extends IKaseLog | ICFSLog>(
  logs: T[],
  searchTerm: string,
): T[] => {
  if (!searchTerm) return logs;

  const lowercasedTerm = searchTerm.toLowerCase();

  return logs.filter((log) => {
    const matchesEvent = log.event.toLowerCase().includes(lowercasedTerm);
    const matchesCreatedBy =
      log.createdBy?.firstName.toLowerCase().includes(lowercasedTerm) ||
      log.createdBy?.lastName.toLowerCase().includes(lowercasedTerm);
    const matchesData =
      log.data &&
      JSON.stringify(log.data?.new || '')
        .toLowerCase()
        .includes(lowercasedTerm);

    return matchesEvent || matchesCreatedBy || matchesData;
  });
};

export const getKeyByValue = (
  mapName: Map<any, any>,
  searchValue: any,
): any | undefined => {
  for (const [key, value] of mapName.entries()) {
    if (value === searchValue) {
      return key;
    }
  }
  return undefined;
};

export const preventSubmit = (event: {
  key: string;
  preventDefault: () => void;
  stopPropagation: () => void;
}) => {
  if (event?.key === 'Enter') {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
};

export const getKeysByValues = (
  values: string[],
  type: { [key: string]: string },
) => {
  const keys = values.map((value: string) => {
    const entry = Object.entries(type).find(([, val]) => val === value);
    return entry ? entry[0] : null;
  });

  return keys;
};

export const createItem = async (
  apiMethod: (data: { name: string }) => Promise<{ data: { _id: string } }>,
  setValue: (name: string, value: any) => void,
  setDependency: (value: string | undefined) => void,
  itemName: string,
  propertyName: string,
) => {
  try {
    const res = await apiMethod({ name: itemName });
    setValue(propertyName, res.data._id);
    setDependency(res.data._id);
  } catch (e: any) {
    setDependency(undefined);
    throw new Error(e.message);
  }
};

export const createItemType = async (
  apiMethod: (data: { type: string }) => Promise<{
    data: {
      _id: string;
      type: string;
    };
  }>,
  setValue: (name: string, value: any) => void,
  setDependency: (value: string | undefined) => void,
  itemType: string,
  propertyName: string,
  returnPropMap = 'type',
) => {
  try {
    const res = await apiMethod({ type: itemType });
    setValue(propertyName, res.data[returnPropMap as keyof typeof res.data]);
    setDependency(res.data._id);
  } catch (e: any) {
    setDependency(undefined);
    throw new Error(e.message);
  }
};

export const createItemModel = async (
  apiMethod: (
    itemUrlId: string,
    model: string,
  ) => Promise<{ data: { _id: string; name: string } }>,
  setValue: (name: string, value: any) => void,
  setDependency: (value: string | undefined) => void,
  modelValue: string,
  propertyName: string,
  itemUrlId: string,
) => {
  try {
    const res = await apiMethod(itemUrlId, modelValue);
    setValue(propertyName, res.data.name);
    setDependency(res.data._id);
  } catch (e: any) {
    setDependency(undefined);
    throw new Error(e.message);
  }
};

export const handlePrint = (selector: string) => {
  window.onafterprint = () => {
    window.location.reload();
  };

  const printContents = document.querySelector(selector)?.innerHTML;

  if (printContents) {
    document.body.innerHTML = printContents;
    window.print();
  }
};

/**
 * Formats narrative content into plain text.
 *
 * Narratives have historically been stored in a Draft.js "raw" format where
 * `text` may be:
 * - an array of blocks (Draft.js blocks)
 * - a RawContentState object { blocks: [...], entityMap: {...} }
 * - a plain string (legacy / transitional)
 *
 * This helper is intentionally defensive to avoid route-crashing runtime errors.
 */
export const formatNarrativeContent = (input: any): string => {
  if (!input) return '';

  // Legacy/transitional: plain string
  if (typeof input === 'string') return input;

  // RawContentState: { blocks: [...] }
  if (input?.blocks && Array.isArray(input.blocks)) {
    return input.blocks.map((block: any) => block?.text ?? '').join('');
  }

  // Blocks array: [...]
  if (Array.isArray(input)) {
    return input.map((block: any) => block?.text ?? '').join('');
  }

  // Some shapes nest blocks under `text`
  if (input?.text?.blocks && Array.isArray(input.text.blocks)) {
    return input.text.blocks.map((block: any) => block?.text ?? '').join('');
  }

  // Best-effort fallback
  return '';
};

export const capitalizeFirstLetter = (value: string | undefined) => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

/**
 * Formats eye color value for display as "KEY - Value" (e.g., "BLK - Black")
 * @param eyeColorValue - The eye color value from DB (e.g., "BLACK")
 * @param fallback - Fallback value if eyeColorValue is empty (default: '')
 * @returns Formatted string like "BLK - Black" or fallback
 */
export const formatEyeColorDisplay = (
  eyeColorValue?: string | null,
  fallback = '',
  showCodeAndDescription = true,
): string => {
  if (!eyeColorValue) return fallback;

  const eyeColorKey = Object.keys(EyeColor).find(
    (key) =>
      EyeColor[key as keyof typeof EyeColor].toUpperCase() ===
      eyeColorValue.toUpperCase(),
  );

  const capitalizedValue = capitalizeFirstLetter(eyeColorValue.toLowerCase());
  return eyeColorKey && capitalizedValue
    ? showCodeAndDescription
      ? `${eyeColorKey} - ${capitalizedValue}`
      : capitalizedValue
    : fallback;
};

export const formatHairColorDisplay = (
  hairColorValue?: string | null,
  fallback = '',
  showCodeAndDescription = true,
): string => {
  if (!hairColorValue) return fallback;

  const hairColorKey = Object.keys(HairColor).find(
    (key) =>
      HairColor[key as keyof typeof HairColor].toLowerCase() ===
      hairColorValue.toLowerCase(),
  );

  return hairColorKey && showCodeAndDescription
    ? `${hairColorKey} - ${hairColorValue}`
    : hairColorValue || fallback;
};

export const formatMaritalStatusDisplay = (
  maritalStatusValue?: string | null,
  fallback = '',
  showCodeAndDescription = true,
): string => {
  if (!maritalStatusValue) return fallback;

  const code = Object.keys(MaritalStatus).find(
    (key) =>
      MaritalStatus[key as keyof typeof MaritalStatus] === maritalStatusValue,
  );

  return code && showCodeAndDescription
    ? `${code} - ${maritalStatusValue}`
    : maritalStatusValue || fallback;
};

export const formatReligionDisplay = (
  religionValue?: string | null,
  fallback = '',
  showCodeAndDescription = true,
): string => {
  if (!religionValue) return fallback;

  const code = Object.keys(ReligionList).find(
    (key) => ReligionList[key as keyof typeof ReligionList] === religionValue,
  );

  return code && showCodeAndDescription
    ? `${code} - ${religionValue}`
    : religionValue || fallback;
};

export const formatEducationDisplay = (
  educationValue?: string | null,
  fallback = '',
  showCodeAndDescription = true,
): string => {
  if (!educationValue) return fallback;

  const code = Object.keys(EducationList).find(
    (key) =>
      EducationList[key as keyof typeof EducationList] === educationValue,
  );

  return code && showCodeAndDescription
    ? `${code} - ${educationValue}`
    : educationValue || fallback;
};

export const formatCountryDisplay = (
  countryValue?: string | null,
  fallback = '',
): string => {
  if (!countryValue) return fallback;

  const code = Object.keys(CountryList).find(
    (key) =>
      CountryList[key as keyof typeof CountryList].toUpperCase() ===
      countryValue.toUpperCase(),
  );

  if (!code) return countryValue || fallback;

  return CountryDisplayNames[code] || countryValue || fallback;
};

export const formatHeightDisplay = (
  heightValue?: IHeightExact | null,
  fallback = '',
): string => {
  if (!heightValue) {
    return fallback;
  }

  const { feet, inches } = heightValue;
  if (feet === null || feet === undefined) {
    return fallback;
  }

  const parsedFeet = Number(feet);
  const parsedInches =
    inches === null || inches === undefined ? 0 : Number(inches);

  if (Number.isNaN(parsedFeet) || Number.isNaN(parsedInches)) {
    return fallback;
  }

  const normalizedInches = Number(parsedInches.toFixed(2));

  return `${parsedFeet}'${normalizedInches}"`;
};

export const heightExactToDecimalFeet = (
  heightValue?: IHeightExact | null,
): number | undefined => {
  if (
    !heightValue ||
    heightValue.feet === null ||
    heightValue.feet === undefined
  ) {
    return undefined;
  }

  const feet = Number(heightValue.feet);
  const inches =
    heightValue.inches === null || heightValue.inches === undefined
      ? 0
      : Number(heightValue.inches);

  if (Number.isNaN(feet) || Number.isNaN(inches)) {
    return undefined;
  }

  return Number((feet + inches / 12).toFixed(2));
};

/**
 * Utility function to check if all selected properties have a specific status or one of multiple statuses.
 * Commonly used for verifying the statuses of properties in Custody Actions.
 *
 * @param {Array<{ status: string }>} bulkSelections - The list of selected properties with a `status` field.
 * @param {string | string[]} statusToCheck - The status or array of statuses to check against.
 *
 * @returns {boolean} - Returns `true` if all selected properties have the specified status(es), otherwise `false`.
 */
export const checkStatus = (
  bulkSelections: Array<{ status: string }>,
  statusToCheck: string | string[],
): boolean => {
  if (bulkSelections.length === 0) return false;

  const statuses = Array.isArray(statusToCheck)
    ? statusToCheck
    : [statusToCheck];

  return bulkSelections.every((item) => statuses.includes(item.status));
};

export const formatAgencyAddress = (agency?: IAgency | null) => {
  if (!agency) return '';

  let address = agency?.address?.address1 ?? '';
  if (agency?.address?.address2) {
    address += `, ${agency?.address?.address2}`;
  }

  if (agency?.address?.city) {
    address += `, ${agency?.address?.city}`;
  }

  if (agency?.address?.state) {
    address += `, ${agency?.address?.state}`;
  }

  if (agency?.address?.zipcode) {
    address += ` ${agency?.address?.zipcode}`;
  }

  return address;
};

/**
 * Checks if a user has access to a specific case.
 *
 * @param {IUser} user - The user object to check access for.
 * @param {IKase} data - The case object containing the list of users with access.
 * @returns {boolean} - Returns true if the user has access to the case, false otherwise.
 */
export const userHasCaseAccess = (user: IUser, data: IKase) => {
  return (
    !data?.caseRestrictedBy ||
    data?.usersWithCaseAccess?.some((u: IUser) => u?._id === user?._id) ||
    data?.caseRestrictedBy?._id === user?._id ||
    data?.createdBy?._id === user?._id
  );
};

/**
 * Generates a unique ID by prefixing a given string or number with a specified prefix.
 *
 * @param {string} prefix - The prefix to be added to the ID.
 * @param {string | number} id - The ID to be prefixed. If it's a number, it will be padded with leading zeros.
 * @returns {string} - The generated unique ID.
 */
export const generateUniqueId = (prefix: string, id: string | number) => {
  if (typeof id === 'number') {
    return `${prefix}${id.toString().padStart(8, '0')}`;
  }
  return id;
};

/**
 * Generates a location marker ID in the format LM-YY-MM-####
 * @param {number} incrementalNumber - The incrementing number for the month (starts from 1)
 * @param {Date} date - Optional date to use for year/month calculation (defaults to current date)
 * @returns {string} - The generated location marker ID
 */
export const generateLocationMarkerId = (
  incrementalNumber: number,
  date: Date = new Date(),
): string => {
  const year = date.getFullYear().toString().slice(-2); // Last 2 digits of year
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month with leading zero
  const number = incrementalNumber.toString().padStart(4, '0'); // 4-digit number with leading zeros

  return `LM-${year}-${month}-${number}`;
};

export const formatUnitName = (
  unitName: string,
  isDeleted?: boolean,
): string => {
  if (!unitName) return '';
  return isDeleted ? `${unitName}-deleted` : unitName;
};
